import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { useLocation } from 'wouter'
import { useUser } from '@/contexts/UserContext'
import { capacitacionesIaApi } from '@/lib/api/capacitaciones-ia'
import type { SidebarBadges } from '@/types/capacitaciones-ia'

/**
 * Conteos livianos de cumplimiento (I1, sdd/gestion-capacitaciones — cierre
 * del módulo, spec §6 regla 4).
 *
 * OJO CON EL NOMBRE: el sidebar ya NO pinta badges — se sacaron porque un
 * número suelto al lado de una etiqueta del menú no dice qué cuenta. El nombre
 * del hook y del tipo se conservan porque son los del endpoint (`GET .../
 * badges`, `BadgesController`). Hoy el único consumidor es el Dashboard, vía
 * `SidebarBadgesProvider` en `AppLayout`.
 *
 * Se refresca en CADA navegación (dependencia de `location`). Con el sidebar
 * como consumidor eso era obligatorio; hoy es la forma más simple de garantizar
 * que el Dashboard esté fresco al volver a él, a costa de un request por cambio
 * de ruta. Si esto llega a pesar, el arreglo es que lo fetchee el Dashboard,
 * no cachear acá.
 *
 * Fallo silencioso: si el request falla, `badges` queda `null` y el consumidor
 * no muestra nada (mismo efecto que "todo en cero"). No hay retry automático:
 * el próximo cambio de ruta vuelve a intentar solo.
 */

/** Señal para refrescar los conteos sin navegar. Mismo mecanismo que
 * `auth:logout` en `lib/api.ts`: el emisor no conoce al consumidor. */
export const BADGES_REFRESH_EVENT = 'badges:refresh'

// eslint-disable-next-line react-refresh/only-export-components
export function useSidebarBadges(): SidebarBadges | null {
  const { user } = useUser()
  const [location] = useLocation()
  const [badges, setBadges] = useState<SidebarBadges | null>(null)

  const fetchBadges = useCallback(async () => {
    if (!user) {
      setBadges(null)
      return
    }
    try {
      setBadges(await capacitacionesIaApi.getBadges())
    } catch {
      setBadges(null)
    }
  }, [user])

  useEffect(() => {
    fetchBadges()
  }, [fetchBadges, location])

  useEffect(() => {
    window.addEventListener(BADGES_REFRESH_EVENT, fetchBadges)
    return () => window.removeEventListener(BADGES_REFRESH_EVENT, fetchBadges)
  }, [fetchBadges])

  return badges
}

/**
 * Los conteos, expuestos al árbol desde `AppLayout` (el único que los fetchea).
 *
 * Nació para que el sidebar y el Dashboard mostraran el MISMO snapshot —regla
 * #1 del spec, "un dato, un número"—: dos llamadas independientes podían dejar
 * el badge del menú y el KPI del Dashboard contradiciéndose en pantalla. Con
 * los badges del menú fuera queda un solo consumidor, así que el provider hoy
 * es sólo el cable entre el layout y el Dashboard. Se conserva porque el
 * request vive en el layout; si se moviera al Dashboard, este contexto sobra.
 */
const SidebarBadgesContext = createContext<SidebarBadges | null>(null)

export function SidebarBadgesProvider({
  value,
  children,
}: {
  value: SidebarBadges | null
  children: React.ReactNode
}) {
  return <SidebarBadgesContext.Provider value={value}>{children}</SidebarBadgesContext.Provider>
}

/** `null` mientras carga, si el request falló, o fuera del provider. */
// eslint-disable-next-line react-refresh/only-export-components
export function useSidebarBadgesContext(): SidebarBadges | null {
  return useContext(SidebarBadgesContext)
}
